import { ButtonBase, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { Genres, Routes } from 'enums';
import PaiLink from 'features/ui/base-components/pai-link';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { makeStyles } from 'tss-react/mui';

interface Props {
  open: boolean;
  genres: Genres[];

  acceptGenre(genre: Genres): () => void;
}

interface GenreItemProps {
  genre: Genres;

  acceptGenre(genre: Genres): () => void;
}

const GenreItem: React.FC<GenreItemProps> = ({ genre, acceptGenre }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <ButtonBase className={classes.genreItem} onClick={acceptGenre(genre)}>
      <Typography className={classes.text}>
        {t(
          genre === Genres.ACTIVE_SOURCING
            ? 'active_sourcing_short'
            : genre === Genres.SALES_EMAIL
            ? 'sales_email_short'
            : genre === Genres.CONNECTION_REQUEST
            ? 'connection_request_short'
            : genre
        )}
      </Typography>
    </ButtonBase>
  );
};

const SelectGenreDialog: React.FC<Props> = ({ open, genres, acceptGenre }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const isLeadBooster = () => {
    return location.pathname.includes(Routes.LEAD_BOOSTER);
  };

  const isHrBooster = () => {
    return location.pathname.includes(Routes.HR_BOOSTER);
  };

  return (
    <Dialog open={open} classes={{ paper: classes.paper }} fullWidth={true} maxWidth={'xs'}>
      <DialogTitle className={classes.titleContainer}>
        <Typography className={classes.titleText}>{t('selectGenreText')}</Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Stack gap={1}>
          {genres.map((genre) => (
            <GenreItem key={genre} genre={genre} acceptGenre={acceptGenre} />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <PaiLink
          text={t('close')}
          size={'sm'}
          underline={'always'}
          action={() => {
            if (isLeadBooster()) {
              return navigate(Routes.LEAD_BOOSTER);
            } else if (isHrBooster()) {
              return navigate(Routes.HR_BOOSTER);
            } else {
              return;
            }
          }}
        />
      </DialogActions>
    </Dialog>
  );
};
const useStyles = makeStyles()((theme) => ({
  paper: {
    borderRadius: 0,
  },
  titleContainer: {
    background: theme.palette.primary.main,
  },
  titleText: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
    textTransform: 'uppercase',
    color: '#FFF',
    textAlign: 'center',
  },
  content: {
    padding: '16px !important',
    [theme.breakpoints.down(400)]: {
      padding: '16px 8px 8px !important',
    },
  },
  genreItem: {
    padding: '8px',
    border: `1px solid ${theme.palette.greys.light}`,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  text: {
    fontSize: theme.typography.pxToRem(15),
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '16px',
    padding: '0 16px 16px 16px',
  },
}));

export default SelectGenreDialog;
